import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FiAlertTriangle as AlertTriangle, FiRefreshCw as RefreshCw,
  FiActivity as Activity, FiBookOpen as BookOpen, FiDatabase as Database,
  FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { useMemoryData } from '../../../../hooks/useMemoryData';
import MemoryOverviewTab from '../components/memory/MemoryOverviewTab';
import MemoryTimelineTab from '../components/memory/MemoryTimelineTab';
import MemoryDetailsTab from '../components/memory/MemoryDetailsTab';
import { WHAT_IS_AGENT_MEMORY } from '../components/memory/memoryTooltips';

const TABS = [
  { id: 'overview', label: 'Health Summary', icon: Activity },
  { id: 'timeline', label: 'Run Stories', icon: BookOpen },
  { id: 'details', label: 'Deep Dive', icon: Database },
];

const HelpBanner = () => {
  const storageKey = 'memory_help_dismissed';
  const [expanded, setExpanded] = useState(() => {
    return localStorage.getItem(storageKey) !== 'true';
  });

  const handleDismiss = () => {
    setExpanded(false);
    localStorage.setItem(storageKey, 'true');
  };

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
      <button
        onClick={() => expanded ? handleDismiss() : setExpanded(true)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
          {WHAT_IS_AGENT_MEMORY.title}
        </span>
        {expanded ? (
          <FiChevronUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        ) : (
          <FiChevronDown className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        )}
      </button>
      {expanded && (
        <div className="px-5 pb-4 space-y-2">
          {WHAT_IS_AGENT_MEMORY.paragraphs.map((p) => (
            <p key={p} className="text-sm text-blue-700 dark:text-blue-300">{p}</p>
          ))}
          <button
            onClick={handleDismiss}
            className="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            Got it, don't show this again
          </button>
        </div>
      )}
    </div>
  );
};

const MemoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get initial values from URL
  const initialTab = searchParams.get('tab') || 'overview';
  const initialRunId = searchParams.get('run') || '';
  const initialType = searchParams.get('type') || '';
  const initialOperation = searchParams.get('op') || '';
  const initialDays = Number.parseInt(searchParams.get('days') || '7', 10);

  const [activeTab, setActiveTab] = useState(initialTab);

  // Use custom hooks for data management
  const {
    snapshots,
    stats,
    loading,
    error,
    currentPage,
    totalPages,
    filters,
    updateFilters,
    clearFilters,
    goToPage,
    refresh,
  } = useMemoryData({
    runId: initialRunId,
    memoryType: initialType,
    operation: initialOperation,
    days: initialDays,
  });

  // Sync URL with state
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'overview') params.set('tab', activeTab);
    if (filters.runId) params.set('run', filters.runId);
    if (filters.memoryType) params.set('type', filters.memoryType);
    if (filters.operation) params.set('op', filters.operation);
    if (filters.days !== 7) params.set('days', filters.days.toString());

    const newSearch = params.toString();
    if (newSearch !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, filters, searchParams, setSearchParams]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleNavigateToRun = (runId: string) => {
    navigate(`/ai-operations/runs/${runId}`);
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
              Agent Memory
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
              Understand how your agents manage their context window and identify optimization opportunities
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Time Range Selector */}
            <select
              value={filters.days}
              onChange={(e) => updateFilters({ days: Number(e.target.value) })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>

            {/* Refresh Button */}
            <button
              onClick={refresh}
              disabled={loading}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700
                       hover:bg-gray-200 dark:hover:bg-gray-600
                       disabled:opacity-50 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Help Banner */}
      <HelpBanner />

      {/* Main Content Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow transition-colors">
        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                  {tab.id === 'details' && snapshots.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {snapshots.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Loading State */}
          {loading && !stats && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Error State */}
          {error && !stats && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <p className="text-red-800 dark:text-red-300">Error loading memory data: {error}</p>
              </div>
              <button
                onClick={refresh}
                className="mt-3 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Tab Content */}
          {!loading || stats ? (
            <>
              {activeTab === 'overview' && (
                <MemoryOverviewTab
                  stats={stats}
                  snapshots={snapshots}
                  loading={loading}
                  filters={filters as any}
                  updateFilters={updateFilters}
                  onNavigateToRun={handleNavigateToRun}
                />
              )}

              {activeTab === 'timeline' && (
                <MemoryTimelineTab
                  snapshots={snapshots}
                  stats={stats}
                  loading={loading}
                  filters={filters as any}
                  updateFilters={updateFilters}
                  clearFilters={clearFilters}
                  onNavigateToRun={handleNavigateToRun}
                />
              )}

              {activeTab === 'details' && (
                <MemoryDetailsTab
                  snapshots={snapshots}
                  loading={loading}
                  filters={filters as any}
                  updateFilters={updateFilters}
                  clearFilters={clearFilters}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  goToPage={goToPage}
                  onNavigateToRun={handleNavigateToRun}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MemoryPage;
